// src/components/admin/UserActivityTab.tsx
import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Skeleton,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Grid,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import { useTranslation } from "../../i18n";
import { fetchData } from "../../api/apiHelpers";
import { athenaDarkPaperSx, athenaBackdropSx, athenaDarkTitleSx, athenaDarkContentSx } from "../layout/AthenaDialogStyles";
import { glass, glassTableSx } from "../../theme/glassTokens";

interface ActivityItem {
  id: number;
  created_at: string;
  user_email: string | null;
  user_id: number | null;
  action: string;
  resource_type: string;
  resource_id: string;
  detail: string;
  ip_address: string | null;
}

interface ActivityResponse {
  results: ActivityItem[];
  count: number;
  num_pages: number;
  page: number;
}

interface UserProfile {
  user: { id: number; email: string; username: string; last_login: string | null };
  organization: { name: string; tier: string } | null;
  period_days: number;
  action_counts: Record<string, number>;
  top_pages: { path: string; count: number }[];
  recent_activity: {
    created_at: string;
    action: string;
    resource_type: string;
    resource_id: string;
    detail: string;
  }[];
}

const ACTION_COLORS: Record<string, "default" | "primary" | "secondary" | "success" | "warning" | "error" | "info"> = {
  visit: "info",
  create: "success",
  update: "primary",
  delete: "error",
  upload: "secondary",
  extract: "secondary",
  query: "primary",
  export: "warning",
  login: "success",
  logout: "default",
};

const UserActivityTab: React.FC = () => {
  const textSecondary = glass.textSecondary;
  const { t } = useTranslation();
  const [data, setData] = useState<ActivityResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [actionFilter, setActionFilter] = useState("");
  const [profileUser, setProfileUser] = useState<number | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);

  const loadActivity = useCallback(async () => {
    setLoading(true);
    try {
      let url = `/billing/admin/activity/?page=${page}&page_size=25`;
      if (actionFilter) url += `&action=${actionFilter}`;
      const resp = await fetchData<ActivityResponse>(url);
      setData(resp);
    } catch {
      // silent fail for log view
    } finally {
      setLoading(false);
    }
  }, [page, actionFilter]);

  useEffect(() => {
    loadActivity();
  }, [loadActivity]);

  const loadProfile = useCallback(async (userId: number) => {
    setProfileUser(userId);
    setProfileLoading(true);
    try {
      const resp = await fetchData<UserProfile>(`/billing/admin/activity/user/${userId}/`);
      setProfile(resp);
    } catch {
      setProfile(null);
    } finally {
      setProfileLoading(false);
    }
  }, []);

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Box>
      {/* Filters */}
      <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>{t("admin.filter_action", "Action")}</InputLabel>
          <Select
            value={actionFilter}
            label={t("admin.filter_action", "Action")}
            onChange={(e) => { setActionFilter(e.target.value); setPage(1); }}
          >
            <MenuItem value="">{t("admin.all_filter", "All")}</MenuItem>
            {["visit", "create", "update", "delete", "upload", "extract", "query", "export", "login", "logout"].map(a => (
              <MenuItem key={a} value={a}>{a}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Activity table */}
      <Box
        sx={{
          p: { xs: 1.5, sm: 2.5 },
          borderRadius: 2,
          background: glass.panel,
          border: `1px solid ${glass.panelBorder}`,
          backdropFilter: "blur(8px)",
          transition: "border-color 0.2s ease",
          "&:hover": { borderColor: glass.headerDivider },
          overflow: "hidden",
        }}
      >
        <TableContainer>
          <Table size="small" sx={glassTableSx}>
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 700, fontSize: "0.75rem", textTransform: "uppercase", color: glass.textSecondary } }}>
                <TableCell>{t("admin.col_time", "Time")}</TableCell>
                <TableCell>{t("admin.col_user", "User")}</TableCell>
                <TableCell>{t("admin.col_action", "Action")}</TableCell>
                <TableCell>{t("admin.col_resource", "Resource")}</TableCell>
                <TableCell>{t("admin.col_detail", "Detail")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading
                ? Array.from({ length: 8 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 5 }).map((_, j) => (
                        <TableCell key={j}><Skeleton /></TableCell>
                      ))}
                    </TableRow>
                  ))
                : data?.results.map((item) => (
                    <TableRow
                      key={item.id}
                      hover
                      sx={{ "&:last-child td": { border: 0 } }}
                    >
                      <TableCell sx={{ whiteSpace: "nowrap", fontSize: "0.8rem" }}>
                        {formatDate(item.created_at)}
                      </TableCell>
                      <TableCell>
                        {item.user_id ? (
                          <Chip
                            size="small"
                            icon={<PersonIcon />}
                            label={item.user_email || `User #${item.user_id}`}
                            variant="outlined"
                            onClick={() => loadProfile(item.user_id!)}
                            sx={{ cursor: "pointer", maxWidth: 200, "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis" } }}
                          />
                        ) : (
                          <Typography variant="caption" sx={{ color: textSecondary }}>system</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={item.action}
                          color={ACTION_COLORS[item.action] || "default"}
                          sx={{ fontWeight: 600, fontSize: "0.7rem" }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.8rem" }}>
                        {item.resource_type}
                        {item.resource_id ? ` #${item.resource_id.slice(0, 20)}` : ""}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.8rem", maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {item.detail}
                      </TableCell>
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
        </TableContainer>
        {data && data.num_pages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <Pagination
              count={data.num_pages}
              page={page}
              onChange={(_, v) => setPage(v)}
              color="primary"
              size="small"
            />
          </Box>
        )}
      </Box>

      {data && (
        <Typography variant="caption" sx={{ color: textSecondary, mt: 1, display: "block" }}>
          {`${data.count.toLocaleString()} total events`}
        </Typography>
      )}

      {/* User Profile Dialog */}
      <Dialog
        open={profileUser !== null}
        onClose={() => setProfileUser(null)}
        maxWidth="sm"
        fullWidth
        slotProps={{ backdrop: { sx: athenaBackdropSx } }}
        PaperProps={{ sx: athenaDarkPaperSx }}
      >
        <DialogTitle sx={{ ...athenaDarkTitleSx, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6" fontWeight={700}>
            {t("admin.user_profile", "User Profile")}
          </Typography>
          <IconButton size="small" onClick={() => setProfileUser(null)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={athenaDarkContentSx}>
          {profileLoading ? (
            <Box sx={{ py: 3 }}>
              <Skeleton height={40} />
              <Skeleton height={40} />
              <Skeleton height={40} />
            </Box>
          ) : profile ? (
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                {profile.user.email}
              </Typography>
              <Typography variant="body2" sx={{ color: textSecondary }}>
                {profile.organization?.name
                  ? `${profile.organization.name} (${profile.organization.tier})`
                  : "No organization"}
              </Typography>
              <Typography variant="body2" sx={{ color: textSecondary, mb: 2 }}>
                Last login: {profile.user.last_login ? new Date(profile.user.last_login).toLocaleString() : "Never"}
              </Typography>

              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                Actions (last {profile.period_days} days)
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                {Object.entries(profile.action_counts).map(([action, count]) => (
                  <Chip
                    key={action}
                    size="small"
                    label={`${action}: ${count}`}
                    color={ACTION_COLORS[action] || "default"}
                    variant="outlined"
                  />
                ))}
              </Box>

              {profile.top_pages.length > 0 && (
                <>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                    Top Pages
                  </Typography>
                  <List dense disablePadding>
                    {profile.top_pages.map((p, i) => (
                      <ListItem key={i} disableGutters sx={{ py: 0.25 }}>
                        <ListItemText
                          primary={p.path}
                          secondary={`${p.count} visits`}
                          primaryTypographyProps={{ variant: "body2" }}
                          secondaryTypographyProps={{ variant: "caption" }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </>
              )}
            </Box>
          ) : (
            <Typography sx={{ color: textSecondary }}>
              {t("admin.profile_error", "Could not load user profile.")}
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default UserActivityTab;
